import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, Hotel, Radio, Clock, Check, AlertTriangle, Star, Car, Waves, CalendarRange } from "lucide-react";
import { eur } from "../lib/api";
import { useI18n, verdictKey } from "../i18n";
import { formatTripDate } from "../lib/goal";
import { saveSelectedLiveTrip } from "../lib/selectedTrip";

// Composed complete-trip section (Real Search V2). Renders when the backend returns
// trip_offers (flight + accommodation). Live-complete = primary; live-partial (live flight
// + controlled hotel) = clearly labelled estimate. Never presents a partial trip as fully live.
export const TripOffers = ({ data, goal }) => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  if (!data || !data.trip_offers?.length) return null;
  const complete = data.mode === "live_complete";

  const time = (iso) => { try { return new Date(iso).toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-GB", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="mb-8" data-testid="trip-offers">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        {complete ? (
          <span className="dv-pill text-xs px-3 py-1.5 font-display" style={{ background: "var(--book)", color: "#fff" }} data-testid="trips-badge">
            <span className="relative flex h-2 w-2 mr-1"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" /><span className="relative inline-flex rounded-full h-2 w-2 bg-white" /></span>
            {t("results.liveBadge")}
          </span>
        ) : (
          <span className="dv-pill text-[10px] px-2.5 py-1 font-display" style={{ background: "var(--paper)", border: "1px solid var(--line)", color: "var(--muted)" }} data-testid="trips-estimate-badge">
            {t("results.tripsEstimateBadge")}
          </span>
        )}
        <h2 className="font-display font-bold text-xl">{complete ? t("results.tripsCompleteTitle") : t("results.tripsEstimateTitle")}</h2>
      </div>
      {!complete && <p className="text-xs text-slate-500 mb-4 max-w-2xl" data-testid="trips-partial-note">{t("results.tripsPartialNote")}</p>}

      <div className="grid md:grid-cols-3 gap-4">
        {data.trip_offers.map((o, i) => {
          const book = o.decision === "BOOK";
          const c = book ? { m: "var(--book)", ink: "var(--book-ink)", soft: "var(--book-soft)" } : { m: "var(--wait)", ink: "var(--wait-ink)", soft: "var(--wait-soft)" };
          const a = o.accommodation_offer;
          const fl = o.flight_offer;
          return (
            <motion.div key={o.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="dv-card p-5 flex flex-col" data-testid={`trip-offer-${i}`} style={{ borderColor: i === 0 ? "var(--book-soft)" : "var(--line)" }}>
              <div className="flex items-center justify-between">
                <span className="dv-pill text-[10px] px-2 py-0.5" style={{ background: "var(--ink)", color: "#fff" }}>{o.origin} → {o.destination}</span>
                {o.badge === "BEST MATCH" && <span className="dv-pill text-[10px] px-2 py-0.5" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}>{t("results.liveBestMatch")}</span>}
              </div>
              <div className="mt-2 font-display font-bold text-base">{o.destination_name || o.destination}</div>
              {(o.departure_date || o.return_date) && (
                <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700" data-testid={`trip-offer-dates-${i}`}>
                  <CalendarRange size={13} color="var(--brand)" />
                  {formatTripDate(o.departure_date, lang)} → {formatTripDate(o.return_date, lang)} · {o.nights || a?.nights} {lang === "fr" ? "nuits" : "nights"}
                </div>
              )}

              {/* Flight line */}
              <div className="mt-3 flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--brand-soft)" }}><Plane size={15} color="var(--brand)" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-slate-800 truncate">{fl.marketing_carriers?.join(" · ") || t("results.tripFlight")}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2">
                    <span>{fl.stops === 0 ? t("results.liveDirect") : t("results.liveStops", { n: fl.stops })}</span>
                    <span className="inline-flex items-center gap-0.5"><Clock size={10} /> {fl.travel_hours}h</span>
                  </div>
                </div>
                <div className="dv-total text-sm text-slate-900 shrink-0">{eur(o.flight_total)}</div>
              </div>

              {/* Hotel line */}
              <div className="mt-2.5 flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--brand-soft)" }}><Hotel size={15} color="var(--brand)" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-slate-800 truncate">{a.name}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                    {a.star_rating != null && <span className="inline-flex items-center gap-0.5">{a.star_rating}<Star size={9} className="fill-current" style={{ color: "var(--wait)" }} /></span>}
                    <span>{t("results.tripNights", { n: a.nights })}</span>
                    {a.pool === true && <span className="inline-flex items-center gap-0.5" style={{ color: "var(--book-ink)" }}><Waves size={10} /> {lang === "fr" ? "piscine" : "pool"}</span>}
                  </div>
                </div>
                <div className="dv-total text-sm text-slate-900 shrink-0">{eur(o.accommodation_total)}</div>
              </div>

              {/* Transfer estimate */}
              <div className="mt-2.5 flex items-center gap-2 text-[11px] text-slate-500">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}><Car size={14} color="var(--muted)" /></div>
                <span className="flex-1">{t("results.tripTransfer")}</span>
                <span className="font-mono-num text-slate-700">{eur(o.transfer_estimate)}</span>
              </div>

              {/* Complete trip total */}
              <div className="mt-4 pt-3 border-t flex items-end justify-between" style={{ borderColor: "var(--line)" }}>
                <div>
                  <div className="text-[11px] text-slate-400">{t("results.tripTotal")}</div>
                  <div className="dv-total text-2xl text-slate-900">{eur(o.trip_total)}</div>
                  <div className="text-[11px]" style={{ color: o.budget_left >= 0 ? "var(--book-ink)" : "var(--alert)" }} data-testid={`trip-budget-${i}`}>
                    {o.budget_left >= 0 ? t("results.tripBudgetLeft", { x: eur(o.budget_left) }) : t("results.tripBudgetOver", { x: eur(-o.budget_left) })}
                  </div>
                </div>
                <span className="dv-pill text-[11px] px-2.5 py-1 font-display" style={{ background: c.soft, color: c.ink }}>
                  {book ? <Check size={12} /> : <AlertTriangle size={11} />} {t(verdictKey(o.decision))}
                </span>
              </div>

              {o.decision_reasons?.[0] && <div className="mt-3 text-xs text-slate-600 flex items-start gap-1.5"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: c.m }} /> {o.decision_reasons[0]}</div>}

              <button
                onClick={() => { saveSelectedLiveTrip(o, goal || {}); navigate("/trip/selected"); }}
                data-testid={`trip-offer-decision-${i}`}
                className="dv-btn dv-btn-ghost w-full text-sm py-2.5 mt-4"
              >
                {t("results.seeDecision")}
              </button>

              {/* Provenance / freshness */}
              <div className="mt-3 pt-3 border-t text-[10px] text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1" style={{ borderColor: "var(--line)" }}>
                <span className="inline-flex items-center gap-1"><Radio size={10} color="var(--book)" /> {t("results.tripFlightVerified", { t: time(o.flight_fetched_at) })}</span>
                <span className="inline-flex items-center gap-1">
                  {o.accommodation_source === "live"
                    ? <><Radio size={10} color="var(--book)" /> {t("results.tripHotelLive", { t: time(o.hotel_fetched_at) })}</>
                    : t("results.tripHotelEstimated")}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
